import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Loader2 } from 'lucide-react';
import type { GeocodeSuggestion } from '@pms/shared';
import { useLocationAutocomplete } from '@/hooks/useLocationAutocomplete';
import { Field, fieldInputClass } from './fields';

interface Props {
  id: string;
  label: string;
  value: string;
  onChange: (text: string) => void;
  onSelect: (s: GeocodeSuggestion) => void;
  required?: boolean;
  hint?: string;
  placeholder?: string;
  maxLength?: number;
}

/**
 * Accessible location combobox (WAI-ARIA 1.2) for the staff property editor's
 * Address field (TASK-146). Self-contained: owns the geocode hook, keeps the
 * host's free-text value in sync, and on select emits the full suggestion so the
 * host can populate city/state/country/zip + hidden lat/lng. Single "Places"
 * group (no augment). Duplicated per app (see apps/storefront).
 */
export function LocationSearchInput({
  id,
  label,
  value,
  onChange,
  onSelect,
  required,
  hint,
  placeholder,
  maxLength,
}: Props) {
  const geo = useLocationAutocomplete({ limit: 8 });
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const results = geo.results;
  const hasRows = results.length > 0;
  const showDropdown =
    focused && value.trim().length >= 2 && (geo.status !== 'idle' || hasRows);

  useEffect(() => {
    setActiveIndex(-1);
  }, [value, geo.status]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    }
    if (showDropdown) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showDropdown]);

  const optionId = (index: number) => `${listboxId}-opt-${index}`;

  function handleInput(text: string) {
    onChange(text);
    geo.setQuery(text);
  }

  function commit(index: number) {
    const s = results[index];
    if (!s) return;
    onSelect(s);
    geo.reset();
    setActiveIndex(-1);
    setFocused(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      if (!hasRows) return;
      e.preventDefault();
      setFocused(true);
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      if (!hasRows) return;
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      if (showDropdown && activeIndex >= 0) {
        e.preventDefault();
        commit(activeIndex);
      }
    } else if (e.key === 'Escape') {
      if (showDropdown) {
        e.preventDefault();
        setFocused(false);
        setActiveIndex(-1);
      }
    }
  }

  return (
    <Field id={id} label={label} required={required} hint={hint}>
      <div ref={rootRef} className="relative">
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          aria-busy={geo.status === 'loading'}
          aria-required={required || undefined}
          value={value}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={maxLength}
          autoComplete="off"
          className={fieldInputClass}
        />

        {geo.status === 'loading' && (
          <Loader2
            className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-gray-400"
            aria-hidden="true"
          />
        )}

        {showDropdown && (
          <ul
            role="listbox"
            id={listboxId}
            aria-label="Places"
            className="absolute left-0 top-full z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
          >
            {!hasRows && geo.status === 'empty' && (
              <li className="px-3 py-2 text-sm text-gray-500" aria-disabled="true">
                No matches for &ldquo;{value.trim()}&rdquo;
              </li>
            )}
            {!hasRows && geo.status === 'error' && (
              <li className="px-3 py-2 text-sm text-gray-500" aria-disabled="true">
                Couldn&rsquo;t load suggestions — you can keep typing
              </li>
            )}
            {results.map((s, idx) => {
              const active = idx === activeIndex;
              return (
                <li
                  key={s.id}
                  id={optionId(idx)}
                  role="option"
                  aria-selected={active}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commit(idx);
                  }}
                  className={`cursor-pointer rounded-md px-3 py-2 text-sm ${
                    active ? 'bg-brand-50 text-gray-900' : 'text-gray-700'
                  }`}
                >
                  <span className="block truncate">{s.label}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Field>
  );
}
